import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Coins,
  Wallet,
  ShieldCheck,
  Star,
  Camera,
  Mic2,
  Radio,
  Mail,
  Gift,
  Flame,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import earnHero from "@/assets/earn-hero.jpg";
import earnEscrow from "@/assets/earn-escrow.jpg";
import { Button } from "@/components/ui/button";
import { WaitlistForm } from "@/components/waitlist-form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { EARN_OG_IMAGE, EARN_OG_IMAGE_ALT, SITE_NAME, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/earn")({
  head: () =>
    buildPageMetadata({
      path: "/earn",
      title: "Earn — Creator Opportunities Coming Soon | BeHot.Now",
      description:
        "Join the BeHot.Now EARN waitlist for creator opportunities, brand collaborations, UGC campaigns, music services and future content rewards.",
      keywords: [
        "earn money as a creator",
        "creator payouts",
        "brand deals",
        "sponsored posts",
        "UGC creator jobs",
        "paid collaborations",
        "monetize your audience",
      ],
      ogTitle: "Earn — turn your audience into income",
      ogDescription:
        "Rate cards, brand collaborations, UGC campaigns and escrow-protected payouts. Coming soon.",
      image: EARN_OG_IMAGE,
      imageAlt: EARN_OG_IMAGE_ALT,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Earn", path: "/earn" },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "BeHot.Now creator earning opportunities",
        serviceType: "Brand collaborations, UGC campaigns and paid creator opportunities",
        url: `${SITE_URL}/earn`,
        provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
      },
    }),
  component: Earn,
});

const SELLABLE_ICONS = [Camera, Star, Mic2, Radio, Mail, Gift];
const STEP_COUNT = 4;
const FAQ_KEYS = ["a", "b", "c", "d", "e", "fee", "f"];

function Earn() {
  const { t } = useT();

  const sellables = SELLABLE_ICONS.map((icon, i) => ({
    icon,
    title: t(`marketing.earn.sellable.${i}.title`),
    body: t(`marketing.earn.sellable.${i}.body`),
    isReferral: i === 5,
  }));

  const steps = Array.from({ length: STEP_COUNT }, (_, i) => ({
    title: t(`marketing.earn.step.${i}.title`),
    body: t(`marketing.earn.step.${i}.body`),
  }));

  return (
    <div className="mx-auto max-w-5xl px-4 py-12">
      <section className="card-surface overflow-hidden p-0">
        <div className="grid items-center gap-6 md:grid-cols-[1.1fr_1fr]">
          <div className="p-8 sm:p-10">
            <Badge variant="secondary" className="uppercase tracking-widest">{t("marketing.earn.badge_soon")}</Badge>
            <h1 className="mt-4 font-display text-4xl font-bold sm:text-5xl">
              {t("marketing.earn.h1_pre")} <span className="text-gradient-hot">{t("marketing.earn.h1_grad")}</span>
            </h1>
            <p className="mt-3 text-muted-foreground">
              {t("marketing.earn.subtitle")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="hot">
                <Link to="/get-hot" search={{ listing: undefined, category: undefined }}>
                  <Flame className="size-4" /> {t("marketing.earn.cta_profile")}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/referrals">
                  <Gift className="size-4" /> {t("marketing.earn.cta_heat")}
                </Link>
              </Button>
            </div>
          </div>
          <img
            src={earnHero}
            alt={t("marketing.earn.hero_alt")}
            width={1600}
            height={900}
            className="h-full w-full object-cover"
          />
        </div>
      </section>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {[
          { k: "0", v: t("marketing.earn.stat1_v") },
          { k: "2 Heat", v: t("marketing.earn.stat2_v") },
          { k: t("marketing.earn.stat3_k"), v: t("marketing.earn.stat3_v") },
        ].map((x) => (
          <div key={x.v} className="card-surface p-5">
            <p className="font-display text-2xl font-bold text-gradient-hot">{x.k}</p>
            <p className="text-sm text-muted-foreground">{x.v}</p>
          </div>
        ))}
      </div>

      <div className="card-surface mt-10 grid items-center gap-0 overflow-hidden md:grid-cols-2">
        <img
          src={earnEscrow}
          alt={t("marketing.earn.escrow_alt")}
          loading="lazy"
          width={1200}
          height={800}
          className="h-full max-h-64 w-full object-cover"
        />
        <div className="p-8">
          <h2 className="font-display text-2xl font-bold">{t("marketing.earn.escrow_title")}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("marketing.earn.escrow_body")}
          </p>
        </div>
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.earn.sellables_heading")}</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {sellables.map((s) => (
          <div key={s.title} className="card-surface p-6">
            <div className="flex items-center justify-between gap-2">
              <s.icon className="size-6 text-success" />
              <Badge variant="outline" className="text-[11px]">
                {s.isReferral ? t("marketing.earn.badge_live") : t("marketing.earn.badge_soon")}
              </Badge>
            </div>
            <p className="mt-3 font-display font-bold">{s.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.earn.steps_heading")}</h2>
      <div className="mt-4 space-y-3">
        {steps.map((s, i) => (
          <div key={s.title} className="card-surface flex gap-4 p-5">
            <div className="rank-numeral text-2xl leading-none">{String(i + 1).padStart(2, "0")}</div>
            <div>
              <p className="font-display font-semibold">{s.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-4 sm:grid-cols-3">
        <div className="card-surface p-6">
          <ShieldCheck className="size-6 text-success" />
          <p className="mt-3 font-display font-bold">{t("marketing.earn.perk1_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("marketing.earn.perk1_body")}
          </p>
        </div>
        <div className="card-surface p-6">
          <Wallet className="size-6 text-primary" />
          <p className="mt-3 font-display font-bold">{t("marketing.earn.perk2_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("marketing.earn.perk2_body")}
          </p>
        </div>

        <div className="card-surface p-6">
          <Coins className="size-6 text-signal" />
          <p className="mt-3 font-display font-bold">{t("marketing.earn.perk3_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("marketing.earn.perk3_body")}
          </p>
        </div>
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.earn.faq_heading")}</h2>
      <Accordion type="single" collapsible className="mt-4">
        {FAQ_KEYS.map((k) => (
          <AccordionItem value={k} key={k}>
            <AccordionTrigger>{t(`marketing.earn.faq.${k}.q`)}</AccordionTrigger>
            <AccordionContent>
              {t(`marketing.earn.faq.${k}.a`)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="card-surface mt-14 p-8 text-center">
        <Coins className="mx-auto size-8 text-success" />
        <h2 className="mt-3 font-display text-2xl font-bold">{t("marketing.earn.final_heading")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("marketing.earn.final_body")}
        </p>
        <WaitlistForm product="earn" cta={t("marketing.earn.waitlist_cta")} />
      </div>
    </div>
  );
}
